import { Client } from '@/features/clients/api/clientsApi';
import { fetchApi } from '@/shared/api/fetchApi';

export interface CarModel {
  id: string;
  slug: string;
  brand_name: string;
  model_name: string;
  transmission_type: string;
  body_type: string;
  description: string;
  image: string;
  image_url: string;
}

export interface Car {
  id: string;
  slug: string;
  color: string;
  model: CarModel;
  release_year: number;
  registration_number: string;
  description: string;
  price: string;
  image: string;
  image_url: string;
}

export interface ExtraService {
  id: string;
  name: string;
  description: string;
  price: string;
  currency: string;
  original_price: string;
  max_per_rent: number;
  amount: number;
}

export interface PiccoloRenting {
  id: string;
  number: number;
  client: string;
  client_readable: string;
  car: string;
  car_readable: string;
  status: 'draft' | 'confirmed' | 'cancelled';
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  address_to_deliver: string;
  address_to_pickup: string;
  total_price: string;
  currency: string;
  original_price: string;
  deliver_latitude: string;
  deliver_longitude: string;
  pickup_latitude: string;
  pickup_longitude: string;
  created_at: string;
  is_actual: boolean;
  parent: string | null;
  record_date_from: string;
  record_date_to: string | null;
  client_data: Client;
}

export interface Renting {
  id: string;
  number: number;
  status: 'draft' | 'confirmed' | 'cancelled';
  client: Client;
  car: Car;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  address_to_deliver: string;
  address_to_pickup: string;
  deliver_latitude: string;
  deliver_longitude: string;
  pickup_latitude: string;
  pickup_longitude: string;
  total_price: string;
  currency: string;
  original_price: string;
  created_at: string;
  parent: string | null;
  extra_services: Array<ExtraService>;
  reports: {
    delivery_to_client: string | null;
    pickup_from_client: string | null;
  };
  activities: {
    delivery_to_client: string | null;
    pickup_from_client: string | null;
  };
  record_date_from: string;
  record_date_to: string | null;
  comments_count: number;
}

export interface RentingsResponse {
  data: Renting[];
  total: number;
  offset: number;
  limit: number;
}

export interface RentingPage {
  id: string;
  number: string;
  client: Client;
  car: Car;
  status: 'draft' | 'confirmed' | 'cancelled';
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  address_to_deliver: string;
  address_to_pickup: string;
  total_price: string;
  price: number;
  currency: string;
  original_price?: number;
  deliver_latitude?: number;
  deliver_longitude?: number;
  pickup_latitude?: number;
  pickup_longitude?: number;
  created_at: string;
  loading_confirm?: boolean;
  loading_cancel?: boolean;
  extra_services: Array<{
    id: string;
    name: string;
    description: string;
    price: string;
    currency: string;
    amount: number;
  }>;
  comments: Array<{
    id: string;
    text: string;
    created_at: string;
    author: string;
    employee: {
      email: string;
    };
    file_url?: string;
  }>;
}

export interface RentingsParams {
  offset?: number;
  limit?: number;
  status?: string;
  car?: string;
  client?: string;
}

export interface RentingComment {
  id: string;
  sender_id: string;
  employee: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    city: string;
    role: string;
    started_working_at: string;
    language: string;
    telegram_username: string;
    is_active: boolean;
  };
  text: string;
  file_name: string;
  created_at: string;
  file_url: string;
}

export interface NewApiRenting {
  id: string;
  number: number;
  status: 'draft' | 'confirmed' | 'cancelled';
  car: {
    id: string;
    slug: string;
    color: string;
    model: {
      id: string;
      slug: string;
      brand_name: string;
      model_name: string;
      transmission_type: string;
      body_type: string;
      description: string;
      image: string;
      image_url: string;
    };
    release_year: number;
    registration_number: string;
    description: string;
    price: string;
    image: string;
    image_url: string;
  };
  client: Client;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  address_to_deliver: string;
  address_to_pickup: string;
  deliver_latitude: string;
  deliver_longitude: string;
  pickup_latitude: string;
  pickup_longitude: string;
  total_price: string;
  currency: string;
  original_price: string;
  created_at: string;
  parent: string | null;
  extra_services: Array<ExtraService>;
  reports: {
    delivery_to_client: string | null;
    pickup_from_client: string | null;
  };
  activities: {
    delivery_to_client: string | null;
    pickup_from_client: string | null;
  };
  record_date_from: string;
  record_date_to: string | null;
  comments: Array<RentingComment>;
}

export interface CreateRentingCommentDto {
  text: string;
  file?: File;
}

export interface UpdateRentingDateTimeDto {
  date: string;
  time: string;
}

export interface UpdateRentingAddressDto {
  address: string;
  latitude: string;
  longitude: string;
}

export interface UpdateRentingDto {
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  address_to_deliver: string;
  address_to_pickup: string;
  deliver_latitude: number;
  deliver_longitude: number;
  pickup_latitude: number;
  pickup_longitude: number;
  extra_services: Array<{
    id: string;
    amount: number;
  }>;
}

export interface UpdateRentingResponse {
  id: string;
  number: number;
  status: 'draft' | 'confirmed' | 'cancelled';
  car: string;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  address_to_deliver: string;
  address_to_pickup: string;
  deliver_latitude: string;
  deliver_longitude: string;
  pickup_latitude: string;
  pickup_longitude: string;
  total_price: string;
  currency: 'usd' | 'eur' | 'gbp';
  created_at: string;
  currency_symbol: string;
  tariff: {
    id: string;
    name: string;
    period: number;
    price: string;
    currency: 'usd' | 'eur' | 'gbp';
  };
  extra_services: Array<{
    id: string;
    name: string;
    description: string;
    price: string;
    currency: 'usd' | 'eur' | 'gbp';
    original_price: string;
    max_per_rent: number;
    amount: number;
    total: string;
  }>;
  warnings: Array<{
    warning_type: string;
    permitted_area_latitude: string;
    permitted_area_longitude: string;
    permitted_area_radius: number;
    out_of_bounds_km: string;
  }>;
}

export const rentingsApi = {
  getRentings: async (params: RentingsParams = {}): Promise<RentingsResponse> => {
    const searchParams = new URLSearchParams();

    searchParams.append('offset', (params.offset || 0).toString());
    searchParams.append('limit', (params.limit || 20).toString());

    if (params.status) searchParams.append('status', params.status);
    if (params.car) searchParams.append('car', params.car);
    if (params.client) searchParams.append('client', params.client);

    return fetchApi<RentingsResponse>(`/api/v1/rentings/list?${searchParams.toString()}`);
  },

  getRentingById: async (id: string): Promise<RentingPage> => {
    return fetchApi<RentingPage>(`/api/v1/rentings/${id}`);
  },

  confirmRenting: async (id: string): Promise<void> => {
    await fetchApi(`/api/v1/rentings/${id}/confirm`, {
      method: 'POST',
    });
  },

  cancelRenting: async (id: string): Promise<void> => {
    await fetchApi(`/api/v1/rentings/${id}/cancel`, {
      method: 'POST',
    });
  },

  getAllRentings: async (): Promise<RentingsResponse> => {
    return fetchApi<RentingsResponse>('/api/v1/rentings/list');
  },

  getRentingDetails: async (id: string): Promise<NewApiRenting> => {
    return fetchApi<NewApiRenting>(`/api/v1/rentings/${id}`);
  },

  createComment: async (rentingId: string, data: CreateRentingCommentDto): Promise<RentingComment> => {
    const formData = new FormData();
    formData.append('text', data.text);
    if (data.file) {
      formData.append('file', data.file);
    }

    return fetchApi<RentingComment>(`/api/v1/rentings/${rentingId}/comment`, {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type here as it will be automatically set with the correct boundary for FormData
      },
    });
  },

  updateRentingStartDateTime: async (rentingId: string, data: UpdateRentingDateTimeDto): Promise<void> => {
    await fetchApi(`/api/v1/rentings/${rentingId}/start-datetime`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  },

  updateRentingEndDateTime: async (rentingId: string, data: UpdateRentingDateTimeDto): Promise<void> => {
    await fetchApi(`/api/v1/rentings/${rentingId}/end-datetime`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  },

  updateRentingDeliveryAddress: async (rentingId: string, data: UpdateRentingAddressDto): Promise<void> => {
    await fetchApi(`/api/v1/rentings/${rentingId}/delivery-address`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  },

  updateRentingPickupAddress: async (rentingId: string, data: UpdateRentingAddressDto): Promise<void> => {
    await fetchApi(`/api/v1/rentings/${rentingId}/pickup-address`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  },

  updateRenting: async (rentingId: string, data: UpdateRentingDto): Promise<UpdateRentingResponse> => {
    return fetchApi<UpdateRentingResponse>(`/api/v1/rentings/${rentingId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  },

  getInvoicePdf: async (rentingId: string): Promise<Blob> => {
    return await fetchApi<Blob>(`/api/v1/test/rentings/render_test_pdf/${rentingId}`, {
      method: 'GET',
      headers: {
        'accept': 'application/pdf',
      },
      credentials: 'include',
    });
  }
};
